<template>
   <b-container>
      <h1 class="mb-3">Tax Return Forms for Gross Receipt</h1>

      <b-row>
         <b-col cols="3">
            <b-list-group>
               <b-list-group-item
                  id="btn-category-federal"
                  :variant="categoryIsActive('federal') ? 'secondary' : null"
                  button
                  @click="selectCategory('federal')"
               >
                  Federal
               </b-list-group-item>
               <b-list-group-item
                  :id="`btn-category-${state.value}`"
                  v-for="state in states"
                  :key="state.value"
                  button
                  :variant="categoryIsActive(state.value) ? 'secondary' : null"
                  @click="selectCategory(state.value)"
               >
                  {{ state.text }}
               </b-list-group-item>
            </b-list-group>
         </b-col>

         <b-col cols="9">
            <p>
               Configure the tax return forms that users will use to provide us with their gross
               receipts info. Users will be asked to provide values for the lines of the Federal
               form that matches their company type and the State forms that match their company's
               states. Users will also be asked to upload a copy of these forms.
            </p>
            <div v-if="selectedCategory" class="forms-wrapper">
               <b-card
                  v-for="(taxReturnForm, index) in taxReturnForms"
                  :key="taxReturnForm.id || index"
                  class="mb-3 tax-return-form-card"
               >
                  <b-form-group label="Form Name">
                     <b-form-input
                        :id="`input-tax-return-name-${index}`"
                        :value="taxReturnForm.taxReturn.name"
                        :state="
                           !$v.taxReturnForms.$each[index].taxReturn.name.$dirty
                              ? null
                              : !$v.taxReturnForms.$each[index].taxReturn.name.$invalid
                        "
                        @input="(val) => updateTaxFormName(index, val)"
                     ></b-form-input>
                  </b-form-group>

                  <b-form-group label="Entity Type">
                     <b-form-select
                        :id="`input-entity-type-${index}`"
                        v-model="taxReturnForm.taxReturn.entityType"
                        :options="entityTypeOptions"
                     ></b-form-select>
                  </b-form-group>

                  <div
                     v-for="(taxReturnLine, lineIndex) in taxReturnForm.taxReturnLines"
                     :key="lineIndex"
                     class="line-wrapper"
                  >
                     <b-form-group label="Line ID">
                        <b-form-input
                           :id="`input-tax-return-line-id-${lineIndex}`"
                           :value="taxReturnLine.id"
                           @input="(val) => updateTaxReturnLineId(index, lineIndex, val)"
                        ></b-form-input>
                     </b-form-group>

                     <b-form-group label="Line Description" class="flex-fill">
                        <b-form-input
                           :id="`input-tax-return-line-name-${lineIndex}`"
                           :value="taxReturnLine.name"
                           :state="
                              !$v.taxReturnForms.$each[index].taxReturnLines.$each[lineIndex].name
                                 .$dirty
                                 ? null
                                 : !$v.taxReturnForms.$each[index].taxReturnLines.$each[lineIndex]
                                      .name.$invalid
                           "
                           @input="(val) => updateTaxReturnLineName(index, lineIndex, val)"
                        ></b-form-input>
                     </b-form-group>

                     <div class="delete-line-button-wrapper">
                        <b-button
                           :id="`btn-remove-tax-return-line-${lineIndex}`"
                           size="sm"
                           variant="danger"
                           @click="deleteLine(index, lineIndex)"
                        >
                           <b-icon icon="trash" class="mt-1"></b-icon>
                        </b-button>
                     </div>
                  </div>

                  <div class="add-line-button-wrapper">
                     <b-button class="dark-button" variant="primary" @click="addNewLine(index)"
                        >+ Add Line</b-button
                     >
                  </div>

                  <div class="action-buttons-wrapper">
                     <b-button
                        :id="`btn-save-form-${index}`"
                        variant="primary"
                        @click="saveForm(taxReturnForm, index)"
                        :disabled="$v.taxReturnForms.$each[index].$invalid"
                     >
                        Save
                     </b-button>
                     <b-button
                        :id="`btn-delete-form-${index}`"
                        variant="danger"
                        @click="deleteForm(index)"
                     >
                        Delete
                     </b-button>
                  </div>
               </b-card>
               <b-button
                  id="add-form-button"
                  class="dark-button"
                  variant="primary"
                  @click="addTaxReturnForm"
                  >+ Add Form</b-button
               >
            </div>
         </b-col>
      </b-row>
   </b-container>
</template>

<script>
import {mapGetters} from 'vuex';
import * as $constants from '@/helpers/constants';
import {minLength, required} from 'vuelidate/lib/validators';

export default {
   name: 'AdminTaxReturns',

   data() {
      return {
         selectedCategory: null,
         taxReturnForms: [],
         loadingForms: false,
      };
   },

   computed: {
      ...mapGetters({
         usStates: 'usStates',
         taxReturnFormsData: 'taxReturn/taxReturnForms',
      }),
      states() {
         return Object.entries(this.usStates)
            .sort((a, b) => {
               const [aKey, aName] = a;
               const [bKey, bName] = b;

               if (aKey === 'XX') {
                  return -1;
               } else if (bKey === 'XX') {
                  return 1;
               }

               if (aName < bName) {
                  return 1;
               } else if (aName < bName) {
                  return -1;
               }
               return 0;
            })
            .map((entry) => {
               const [key, value] = entry;
               return {text: value, value: key};
            });
      },

      entityTypeOptions() {
         return [
            {text: 'None', value: $constants.EntityType.NONE},
            {text: 'S-Corp', value: $constants.EntityType.S_CORP},
            {text: 'C-Corp', value: $constants.EntityType.C_CORP},
            {text: 'Partnership', value: $constants.EntityType.PARTNERSHIP},
         ];
      },
   },

   methods: {
      async selectCategory(category) {
         if (this.loadingForms) {
            return;
         }

         this.loadingForms = true;

         // global three-dots PageLoad loader
         this.$emit('showPageload', true);

         try {
            await this.$store.dispatch('taxReturn/loadTaxReturnForms', {category});
            this.selectedCategory = category;
         } finally {
            this.loadingForms = false;
            this.$emit('showPageload', false);
         }
      },

      categoryIsActive(category) {
         return this.selectedCategory === category;
      },

      addNewLine(index) {
         // Ensure reactive properties for Vuelidate by initializing id and name
         this.taxReturnForms[index].taxReturnLines.push({id: '', name: ''});
      },

      deleteLine(index, lineIndex) {
         this.taxReturnForms[index].taxReturnLines.splice(lineIndex, 1);
      },

      addTaxReturnForm() {
         this.taxReturnForms.push({taxReturn: {name: '', entityType: null}, taxReturnLines: []});
      },

      updateTaxFormName(index, value) {
         this.$set(this.taxReturnForms[index].taxReturn, 'name', value);
      },

      updateTaxReturnLineId(index, lineIndex, value) {
         this.$set(this.taxReturnForms[index].taxReturnLines[lineIndex], 'id', value);
      },

      updateTaxReturnLineName(index, lineIndex, value) {
         this.$set(this.taxReturnForms[index].taxReturnLines[lineIndex], 'name', value);
      },

      async saveForm(taxReturnForm, index) {
         const data = await this.$store.dispatch('taxReturn/saveTaxReturnForm', {
            taxReturnForm,
            category: this.selectedCategory,
         });
         this.$set(this.taxReturnForms[index], 'id', data.id);
         this.$bvToast.toast('Tax return data saved successfully!', {
            title: 'Success',
            variant: 'success',
            solid: true,
         });
      },

      async deleteForm(index) {
         if (this.taxReturnForms[index].id) {
            await this.$store.dispatch('taxReturn/deleteTaxReturnForm', {
               taxReturnFormId: this.taxReturnForms[index].id,
            });
         }
         this.taxReturnForms.splice(index, 1);
      },
   },

   watch: {
      taxReturnFormsData(newValue) {
         this.taxReturnForms = newValue;
      },
   },

   validations: {
      taxReturnForms: {
         $each: {
            taxReturn: {
               name: {
                  required,
               },
            },
            taxReturnLines: {
               required,
               minLength: minLength(1),
               $each: {
                  id: {
                     required,
                  },
                  name: {
                     required,
                  },
               },
            },
         },
      },
   },
};
</script>

<style scoped lang="scss">
.forms-wrapper {
   display: flex;
   flex-direction: column;
   justify-content: center;
   gap: 1rem;
}

.tax-return-form-card {
   display: flex;
   flex-direction: column;
   gap: 0.5rem;
}

.line-wrapper {
   display: flex;
   gap: 0.5rem;
}

.add-line-button-wrapper {
   display: flex;
   align-items: flex-start;
}

.delete-line-button-wrapper {
   display: flex;
   align-items: center;
   margin-top: 1rem;
}

.action-buttons-wrapper {
   display: flex;
   align-items: flex-start;
   gap: 0.5rem;
   padding-top: 1rem;
}
</style>
